import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SpotlightTour, type SpotlightStep } from '@/components/onboarding/SpotlightTour';
import {
  clearDashboardTourReplayRequest,
  claimSessionPrompt,
  hasLocalDashboardTourDone,
  isDashboardTourReplayRequested,
  isDashboardTourDone,
  markDashboardTourDone,
} from '@/utils/firstRun';

interface DashboardTourProps {
  /** Wait until the dashboard data has rendered and no blocking dialog is open. */
  enabled: boolean;
  userId?: string | null;
  completedAt?: string | null;
  onCompleted?: (completedAt: string) => void;
}

export const DashboardTour: React.FC<DashboardTourProps> = ({ enabled, userId, completedAt, onCompleted }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [replayMode, setReplayMode] = useState(false);

  useEffect(() => {
    if (!enabled || open) return;
    const replayRequested = isDashboardTourReplayRequested();

    if (!replayRequested && isDashboardTourDone(completedAt, userId)) {
      if (!completedAt && hasLocalDashboardTourDone(userId) && userId) {
        markDashboardTourDone(userId).then(onCompleted);
      }
      return;
    }

    if (!replayRequested && !claimSessionPrompt('tour')) return;
    // Small delay so widgets have laid out before we measure targets.
    const timer = window.setTimeout(() => {
      setReplayMode(replayRequested);
      setOpen(true);
    }, 600);
    return () => window.clearTimeout(timer);
  }, [completedAt, enabled, onCompleted, open, userId]);

  const steps: SpotlightStep[] = [
    { target: 'welcome-banner', title: t('tour.steps.welcome.title'), body: t('tour.steps.welcome.body') },
    { target: 'quick-actions', title: t('tour.steps.quickActions.title'), body: t('tour.steps.quickActions.body') },
    { target: 'first-run-checklist', title: t('tour.steps.checklist.title'), body: t('tour.steps.checklist.body') },
    { target: 'upcoming-matches', title: t('tour.steps.matches.title'), body: t('tour.steps.matches.body') },
    { target: 'active-polls', title: t('tour.steps.polls.title'), body: t('tour.steps.polls.body') },
    { target: 'challenge-matches', title: 'Challenge Matches', body: 'Open Compete to find and follow your pair challenges.' },
  ];

  return (
    <SpotlightTour
      steps={steps}
      open={open}
      onClose={() => {
        clearDashboardTourReplayRequest();
        if (!replayMode || !completedAt) {
          markDashboardTourDone(userId).then(onCompleted);
        }
        setReplayMode(false);
        setOpen(false);
      }}
    />
  );
};

export default DashboardTour;
